/**
 * The small controls the environment rows are built from, in the Workers
 * surface's shape: one `wrk-srow` per setting, the title and its one line of
 * help on the left, the control on the right.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { WorkerConfigField } from "@yagni/shared";
import { ConfigControl } from "../workers/ConfigControl.js";
import { isVariableName } from "./environmentPage.js";

export function Row({
  title,
  help,
  helpId,
  children,
  testId,
}: {
  title: string;
  help?: ReactNode;
  /** Lets a control in the row point `aria-describedby` at this help line. */
  helpId?: string;
  children?: ReactNode;
  testId?: string;
}) {
  return (
    <div className="wrk-srow" data-testid={testId}>
      <span className="wrk-srow-text">
        <span className="wrk-srow-title">{title}</span>
        {help && (
          <span id={helpId} className="wrk-srow-help">
            {help}
          </span>
        )}
      </span>
      {children}
    </div>
  );
}

/**
 * The name of another settings row, said in a sentence, as the button that
 * opens it. Without a way to open rows (a component rendered on its own) it
 * is just the name.
 */
export function RowLink({
  section,
  children,
  onOpenRow,
}: {
  section: string;
  children: ReactNode;
  onOpenRow?: (section: string) => void;
}) {
  if (!onOpenRow) return <>{children}</>;
  return (
    <button type="button" className="wrk-quiet-btn qa-row-link" onClick={() => onOpenRow(section)}>
      {children}
    </button>
  );
}

/**
 * One scalar setting, rendered through the Workers pane's control so the two
 * surfaces agree on what a select, a number and a line of text look like. No
 * provenance rides these: an environment has one level, not a delta chain.
 */
export function ConfigRow({
  field,
  value,
  disabled,
  onChange,
  testId,
}: {
  field: WorkerConfigField;
  value: unknown;
  disabled?: boolean;
  onChange: (value: unknown) => void;
  testId?: string;
}) {
  return (
    <Row title={field.label} help={field.help} testId={testId}>
      <ConfigControl field={field} value={value} disabled={disabled} onChange={onChange} />
    </Row>
  );
}

/**
 * A list edited as lines and committed on blur, so a half-typed line is never
 * filtered away under the reader's cursor.
 */
export function LinesControl({
  label,
  value,
  rows = 3,
  placeholder,
  disabled,
  onCommit,
}: {
  label: string;
  value: string[];
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  onCommit: (next: string[]) => void;
}) {
  const [text, setText] = useState(value.join("\n"));
  const lastSynced = useRef(value.join("\n"));
  useEffect(() => {
    const joined = value.join("\n");
    if (joined !== lastSynced.current) {
      lastSynced.current = joined;
      setText(joined);
    }
  }, [value]);
  return (
    <textarea
      className="wrk-control wrk-control--multiline"
      aria-label={label}
      rows={rows}
      placeholder={placeholder}
      value={text}
      disabled={disabled}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        lastSynced.current = text;
        onCommit(text.split("\n"));
      }}
    />
  );
}

/** One line per variable, `NAME=value`. */
export function formatVariables(variables: Record<string, string> | undefined): string[] {
  return Object.entries(variables ?? {}).map(([name, value]) => `${name}=${value}`);
}

/** The longest value the environment schema keeps (`qaVariables`). */
const MAX_VARIABLE_VALUE = 4096;

/**
 * A line that was not taken, and why. It carries the line's number and the
 * name before its `=` when there is one, never the value: a rejected line may
 * still hold a secret, and the page must not print it back.
 */
export interface VariableReject {
  line: number;
  name?: string;
  reason: string;
}

export interface ParsedVariables {
  variables: Record<string, string> | undefined;
  rejects: VariableReject[];
}

/**
 * `NAME=value` lines, one per variable. A line that cannot be a variable is
 * listed in `rejects` with its reason instead of silently disappearing.
 */
export function parseVariables(lines: string[]): ParsedVariables {
  const entries: [string, string][] = [];
  const rejects: VariableReject[] = [];
  lines.forEach((raw, index) => {
    const line = raw.trim();
    if (!line) return;
    const split = line.indexOf("=");
    if (split === -1) {
      rejects.push({ line: index + 1, reason: "No = between a name and a value" });
      return;
    }
    const name = line.slice(0, split).trim();
    if (!isVariableName(name)) {
      rejects.push({
        line: index + 1,
        reason: "Not a variable name: letters, digits and _ only, not starting with a digit",
      });
      return;
    }
    const value = line.slice(split + 1);
    if (value.length > MAX_VARIABLE_VALUE) {
      rejects.push({ line: index + 1, name, reason: "Value is longer than 4096 characters" });
      return;
    }
    entries.push([name, value]);
  });
  return {
    variables: entries.length ? Object.fromEntries(entries) : undefined,
    rejects,
  };
}

/**
 * A pasted `.env` file. Blank lines and `#` comments are skipped, an `export `
 * prefix is dropped, matching single or double quotes are removed, and an
 * unquoted value loses a trailing ` # comment`. Everything else that cannot be
 * a variable is rejected with its reason. Empty values are rejected too: a
 * credential with no value is not a credential.
 */
export function parseDotEnv(text: string): {
  variables: Record<string, string>;
  rejects: VariableReject[];
} {
  const variables: Record<string, string> = {};
  const rejects: VariableReject[] = [];
  text.split(/\r?\n/).forEach((raw, index) => {
    const number = index + 1;
    let line = raw.trim();
    if (!line || line.startsWith("#")) return;
    if (/^export\s+/.test(line)) line = line.replace(/^export\s+/, "");
    const split = line.indexOf("=");
    if (split === -1) {
      rejects.push({ line: number, reason: "No = between a name and a value" });
      return;
    }
    const name = line.slice(0, split).trim();
    if (!isVariableName(name)) {
      rejects.push({
        line: number,
        reason: "Not a variable name: letters, digits and _ only, not starting with a digit",
      });
      return;
    }
    let value = line.slice(split + 1).trim();
    const quote = value[0];
    if (quote === '"' || quote === "'") {
      const end = value.indexOf(quote, 1);
      if (end === -1) {
        rejects.push({
          line: number,
          name,
          reason: "The quote is never closed. Values over several lines are not supported",
        });
        return;
      }
      value = value.slice(1, end);
    } else {
      value = value.replace(/\s+#.*$/, "");
    }
    if (!value) {
      rejects.push({ line: number, name, reason: "No value" });
      return;
    }
    if (value.length > MAX_VARIABLE_VALUE) {
      rejects.push({ line: number, name, reason: "Value is longer than 4096 characters" });
      return;
    }
    if (name in variables)
      rejects.push({ line: number, name, reason: "Named again; this later line is used" });
    variables[name] = value;
  });
  return { variables, rejects };
}

/** A name that reads like a secret, and so belongs in Credentials. */
export function looksSecret(name: string): boolean {
  return /KEY|TOKEN|SECRET|PASSWORD/i.test(name);
}

/** The rejected lines, each with its reason. Values are never shown. */
export function VariableRejects({ rejects, label }: { rejects: VariableReject[]; label: string }) {
  if (!rejects.length) return null;
  return (
    <ul className="qa-rejects" aria-label={label}>
      {rejects.map((reject) => (
        <li key={`${reject.line}:${reject.reason}`}>
          Line {reject.line}
          {reject.name ? ` (${reject.name})` : ""}: {reject.reason}
        </li>
      ))}
    </ul>
  );
}

/**
 * Plaintext variables for a service or a dependency, as `NAME=value` lines.
 * Lines that cannot be variables are listed with their reasons after the
 * control commits, and a variable whose name reads like a secret offers to
 * move itself into Credentials, where the value is write-only.
 */
export function VariablesControl({
  label,
  value,
  onCommit,
  onStoreCredential,
}: {
  label: string;
  value: Record<string, string> | undefined;
  onCommit: (next: Record<string, string> | undefined) => void;
  /** Moves one variable into Credentials: the caller stages it and drops it here. */
  onStoreCredential?: (name: string, value: string) => void;
}) {
  const [rejects, setRejects] = useState<VariableReject[]>([]);
  const secretLooking = Object.entries(value ?? {}).filter(([name]) => looksSecret(name));
  return (
    <span className="qa-variables">
      <LinesControl
        label={label}
        placeholder="NAME=value"
        value={formatVariables(value)}
        onCommit={(lines) => {
          const parsed = parseVariables(lines);
          setRejects(parsed.rejects);
          onCommit(parsed.variables);
        }}
      />
      <VariableRejects rejects={rejects} label={`Lines not used in ${label}`} />
      {onStoreCredential &&
        secretLooking.map(([name, secret]) => (
          <span className="qa-nudge" key={name}>
            {name} looks like a secret, and these values are shown to anyone who can open this page.{" "}
            <button
              type="button"
              className="wrk-quiet-btn"
              onClick={() => onStoreCredential(name, secret)}
            >
              Store {name} as a credential
            </button>
          </span>
        ))}
    </span>
  );
}
